/**
 * The one place that constructs Firebase.
 *
 * Everything the browser touches is here: Auth, Firestore, Storage and the
 * callable-function client. Nothing privileged is — enrollment, mail, push and
 * retries are callables into ./functions, which run under the Admin SDK where
 * a client cannot reach them.
 *
 * The web config below is public by design. It identifies the project; it does
 * not authorize anything. Security Rules do that.
 */
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

import { ALLOWED_DOMAIN } from '../../shared/authz';
import {
  getSsoHint,
  markSilentSignInAttempted,
  clearSsoHint,
  PORTAL_SIGNOUT_URL,
} from './sso';

const config = {
  apiKey: import.meta.env.VITE_FB_API_KEY,
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FB_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FB_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FB_SENDER_ID,
  appId: import.meta.env.VITE_FB_APP_ID,
};

export const app: FirebaseApp = getApps()[0] ?? initializeApp(config);

export const auth = getAuth(app);

/**
 * Firestore, with an on-disk cache where the platform can manage one.
 *
 * The persistent cache turns a cold open into a warm one and lets the app show
 * something truthful offline — worth having on a phone on a campus network.
 *
 * But it cannot be assumed. iOS Safari refuses IndexedDB outright in private
 * browsing, and the *multi-tab* manager additionally needs cross-tab
 * coordination that Safari does not reliably provide — including inside an
 * installed PWA. When that fails, `initializeFirestore` throws at module load,
 * which takes the whole app down before it renders: the failure looks like
 * "nothing loads on my iPhone" rather than like a caching problem.
 *
 * So it degrades in steps instead. Multi-tab, then single-tab, then Firestore's
 * default in-memory cache. Only the offline convenience is lost; every read
 * still works.
 */
export const db: Firestore = createFirestore();

function createFirestore(): Firestore {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch {
    // Cross-tab coordination unavailable — common on Safari and in standalone
    // PWAs. A single-tab lease is still a real on-disk cache.
    try {
      return initializeFirestore(app, { localCache: persistentLocalCache() });
    } catch {
      // No IndexedDB at all: private browsing, or storage blocked entirely.
      console.warn('[firestore] persistent cache unavailable; running in memory');
      return getFirestore(app);
    }
  }
}

export const storage = getStorage(app);

export const functions = getFunctions(app, import.meta.env.VITE_FUNCTIONS_REGION ?? 'us-central1');

// ── Sign-in ──────────────────────────────────────────────────────────────────

function googleProvider(extra: Record<string, string> = {}): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  // `hd` filters the account chooser to the family domain. It is a convenience,
  // not a control: the check below and the Security Rules are the control.
  provider.setCustomParameters({ hd: ALLOWED_DOMAIN, ...extra });
  return provider;
}

export function emailIsOnDomain(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);
}

/**
 * Interactive sign-in.
 *
 * An account from outside the family domain is signed straight back out. It
 * would be refused by the rules anyway, but leaving it signed in produces the
 * worst possible screen: a working app with nothing in it.
 */
export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, googleProvider());
  if (!emailIsOnDomain(result.user.email)) {
    await firebaseSignOut(auth);
    throw new Error(`SamuelSchool is limited to @${ALLOWED_DOMAIN} accounts.`);
  }
  return result.user;
}

/**
 * Silent sign-in for arrivals from the family portal.
 *
 * A redirect, not a popup: this runs on page load with no click behind it, and
 * browsers block popups no gesture opened. Returns false when there is no hint
 * to act on; otherwise it navigates away and does not return.
 */
export async function trySilentSignIn(): Promise<boolean> {
  const hint = getSsoHint();
  if (!hint) return false;

  markSilentSignInAttempted();
  await signInWithRedirect(auth, googleProvider({ login_hint: hint }));
  return true;
}

/**
 * Finish a redirect sign-in.
 *
 * Firebase resolves this during initialisation anyway; calling it explicitly
 * is the only way to see *why* one failed. The usual cause is the credential
 * handoff being blocked by browser storage partitioning, which is otherwise
 * indistinguishable from never having tried.
 */
export async function completeRedirectSignIn(): Promise<User | null> {
  try {
    const result = await getRedirectResult(auth);
    return result?.user ?? null;
  } catch (err) {
    console.warn('[sso] redirect sign-in failed:', (err as { code?: string })?.code);
    return null;
  }
}

/** End the session here and at the portal. */
export async function signOut(): Promise<void> {
  clearSsoHint();
  try {
    await firebaseSignOut(auth);
  } catch (err) {
    // Never strand someone on a failed sign-out; leaving is the point.
    console.warn('[auth] sign-out failed:', (err as { code?: string })?.code);
  }
  window.location.href = PORTAL_SIGNOUT_URL;
}

/** End the session without leaving, so the page can explain why. */
export async function signOutLocal(): Promise<void> {
  clearSsoHint();
  try {
    await firebaseSignOut(auth);
  } catch {
    /* the guard will still refuse to render */
  }
}

/**
 * The photo to actually display, or null to fall back to initials.
 *
 * `user.photoURL` is a snapshot taken when the account record was created and
 * goes stale; the provider entry carries the current one. Google also serves a
 * generic silhouette for accounts with no picture, which looks broken next to
 * a real face, so treat that as absent.
 */
export function profilePhotoUrl(user: User | null | undefined): string | null {
  if (!user) return null;
  const fromProvider = user.providerData.map((p) => p?.photoURL).find(Boolean);
  const url = fromProvider ?? user.photoURL;
  if (!url || url.includes('/default-user')) return null;
  return url;
}

export { onAuthStateChanged };
export type { User };
